import { compact, uniq } from 'lodash/fp';

import { ScreenStepConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import { isScreenStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import { uiStepAllComponentConfigs } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { Event, EventData } from 'dashboard/models/event';
import { InquiryEvent } from 'dashboard/models/inquiry';
import { DirectWorkflowRunEvent } from 'dashboard/models/inquiry-run';
import { InquiryTemplateVersionStep } from 'dashboard/models/inquiry-template-version';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun, WorkflowRunEvent } from 'dashboard/models/workflow-run';
import { InquiryRunActors, RunStepConfig } from 'dashboard/redux/slices/inquiry-run';
import { getAllUsers } from 'dashboard/redux/thunk-actions/user';
import { EventName } from 'lib/constants/events';
import { InquiryRunPermissionMap } from 'lib/hooks/useInquiryRunPermissionMap';
import { ApiEndpoint, ApiModel, PaginationParams } from 'lib/interfaces/api';
import { UiStepType } from 'lib/interfaces/StepsConfig';
import { isUiTransitionComponentConfig } from 'lib/interfaces/ui-components';
import { Permission } from 'lib/permissions';
import { captureMessage } from 'lib/reporting';
import { assertNever } from 'lib/utils/types';

const fetchInquiryRunData = async ({
  inquiryId,
  environmentId,
  inquiryRunPermissionMap,
}: {
  inquiryId: string;
  environmentId: string;
  inquiryRunPermissionMap: InquiryRunPermissionMap;
}): Promise<{
  events: Event[];
  actors: InquiryRunActors;
}> => {
  const events = await loadInquiryRunEventsData(inquiryId, environmentId);
  const actors = await loadInquiryRunEventsActors(events, environmentId, inquiryRunPermissionMap);

  return {
    events,
    actors,
  };
};

async function loadInquiryRunEventsData(
  inquiryId: string,
  environmentId: string,
): Promise<Event[]> {
  const {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    meta: { events },
  } = await SessionClientDashboard.getResourceMeta<{
    events: Event[];
  }>(ApiEndpoint.Inquiry, `${inquiryId}/run-events`, {
    headers: {
      'Persona-Environment-ID': environmentId,
    },
  });
  // Some events may not have payloads if the current user doesn't have permission to see those payloads.
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return events.filter((event) =>
    Object.prototype.hasOwnProperty.call(event.attributes, 'payload'),
  );
}

/**
 * Helper which returns ids of workflow runs displayed in the
 * inquiry run timeline, which are fetched to support deep-linking
 * from the timeline and displaying additional context
 */
function getAssociatedWorkflowRunIds(runEvents: Event[]): string[] {
  const workflowRunIds = runEvents
    .map((event) => event.attributes.payload.data)
    .filter(
      (eventPayload: EventData): eventPayload is WorkflowRunEvent =>
        eventPayload.type === ApiModel.WorkflowRun,
    )
    .map((workflowRunEvent) => workflowRunEvent.id);
  /* Borrowed from ConnectedEventTimeline:94
   * We can't look at included because creators and reviewers are not
   * included because they are not a formal association on inquiries/cases
   *
   * V_2021_07_05 does a replacement of the reviewer field.
   * Instead of being placed under `attributes` its placed under `relationships`.
   * We need to look at both for backwards compatibility.
   */
  const workflowRunInquiryReviewersFromAttributes = runEvents
    .map((event) => event.attributes.payload.data)
    .filter(
      (eventPayload: EventData): eventPayload is InquiryEvent =>
        eventPayload.type === ApiModel.Inquiry,
    )
    .filter((inquiryEvent) => inquiryEvent.attributes['reviewer-type'] === ApiModel.WorkflowRun)
    .map((inquiryEvent) => inquiryEvent.attributes.reviewer);
  const workflowRunInquiryReviewersFromRelationships = runEvents
    .map((event) => event.attributes.payload.data)
    .filter(
      (eventPayload: EventData): eventPayload is InquiryEvent =>
        eventPayload.type === ApiModel.Inquiry,
    )
    .filter(
      (inquiryEvent) => inquiryEvent.relationships.reviewer?.data?.type === ApiModel.WorkflowRun,
    )
    .map((inquiryEvent) => inquiryEvent.relationships.reviewer.data.id);
  return uniq([
    ...workflowRunIds,
    ...workflowRunInquiryReviewersFromAttributes,
    ...workflowRunInquiryReviewersFromRelationships,
  ]);
}

/**
 * Fetches 'actors' which may update the Inquiry during its lifecycle,
 * such as dashboard users, workflows, and workflow runs.
 *
 * These are used in the timeline to support deep-linking and to
 * display additional context
 */
async function loadInquiryRunEventsActors(
  runEvents: Event[],
  environmentId: string,
  inquiryRunPermissionMap: InquiryRunPermissionMap,
): Promise<InquiryRunActors> {
  const users = await getAllUsers();

  let workflowRuns: WorkflowRun[] = [];
  if (inquiryRunPermissionMap[Permission.WorkflowRunList]) {
    const allRunIds = getAssociatedWorkflowRunIds(runEvents);
    if (allRunIds.length) {
      const response = await SessionClientDashboard.listResource<WorkflowRun, PaginationParams>(
        ApiEndpoint.WorkflowRun,
        {
          filter: {
            'workflow-run-ids': allRunIds,
          },
          page: {
            size: 100,
          },
          headers: {
            'Persona-Environment-ID': environmentId,
          },
        },
      );
      workflowRuns = response.data;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (response.meta['total-pages'] > 1) {
        captureMessage(
          'Page count for inquiry run workflow run fetch exceed 1 with page size 100',
          'warning',
        );
      }
    }
  }

  let workflows: Workflow[] = [];
  if (workflowRuns.length > 0 && inquiryRunPermissionMap[Permission.WorkflowView]) {
    const response = await SessionClientDashboard.listResource<Workflow>(ApiEndpoint.Workflow, {
      filter: {
        'workflow-ids': workflowRuns.map((run) => run.relationships.workflow.data.id),
      },
    });
    workflows = response.data;
  }

  return {
    users,
    workflows,
    workflowRuns,
  };
}

/**
 * Returns array of runStepConfigs for transition and chosen conditional events
 * which are then used to construct a run step tree in ConnectedInquiryRun.
 *
 * We set these values but not the runStepTreeConfigs here because for the
 * step-through experience logic we'll want this array to set the index/current run step
 * while we reconstruct the runStepTree from the subset of this array in the component's local state
 */
export const getRunStepConfigs = (
  events: Event[],
  steps: InquiryTemplateVersionStep[],
): RunStepConfig[] => {
  const initialStep = steps.find((step) => step.config.statuses.some((status) => status.initial));
  const createdAtEvent = events.find((event) => event.attributes.name === EventName.InquiryCreated);

  let initialConfig;
  if (initialStep && createdAtEvent) {
    // This ensures that the run tree has at least the initial step highlighted and can be rendered
    initialConfig = {
      token: initialStep.name,
      prevToken: '',
      timestampToken: `${initialStep.name}.${createdAtEvent.attributes['created-at']}`,
    };
  }

  return compact(
    [initialConfig].concat(
      events.filter(isInquiryRunEvent).map((event) => {
        const { 'created-at': createdAt, name, payload } = event.attributes;
        if (name === EventName.InquiryTransitioned) {
          const { prevStepName, nextStepName } = getTransitionPayload(payload.data as InquiryEvent);
          const prevStep = steps.find(
            (step): step is ScreenStepConfig => isScreenStep(step) && step.name === prevStepName,
          );
          return {
            token: nextStepName,
            prevToken: prevStepName,
            timestampToken: `${nextStepName}.${createdAt}`,
            isTransitionBack:
              prevStep?.config &&
              'back-step' in prevStep.config &&
              prevStep.config['back-step'] === nextStepName,
          };
        } else if (name === EventName.InquiryRunConditionalRouted) {
          const { treeNodeToken, routeIndex, workflowVersionId } = getConditionalPayload(
            payload.data as DirectWorkflowRunEvent,
          );
          return {
            token: treeNodeToken,
            prevToken: getConditionalPrevToken(workflowVersionId, steps),
            timestampToken: `${treeNodeToken}.${createdAt}`,
            chosenIndex: routeIndex - 1,
          };
        }
      }),
    ),
  );
};

const getConditionalPrevToken = (
  workflowVersionId: string,
  steps: InquiryTemplateVersionStep[],
): string => {
  return (
    steps.find((step) => {
      switch (step.type) {
        case UiStepType.Complete:
          return false;
        case UiStepType.Document:
        case UiStepType.Integration:
        case UiStepType.VerifyWithPersona:
        case UiStepType.GovernmentId:
        case UiStepType.Selfie:
        case UiStepType.SelfieReflection:
          return step.config['workflow-version-id'] === workflowVersionId;
        case UiStepType.Ui:
          return uiStepAllComponentConfigs(step)
            .filter(isUiTransitionComponentConfig)
            .map(({ config }) => config['workflow-version-id'])
            .filter((wfvId) => wfvId != null)
            .includes(workflowVersionId);
        default:
          assertNever;
      }
    })?.name ?? ''
  );
};

export type TransitionPayload = {
  prevStepName: string;
  nextStepName: string;
};

export type ConditionalPayload = {
  conditionalName: string;
  routeIndex: number;
  treeNodeToken: string;
  workflowName: string;
  workflowRunId: string;
  workflowVersionId: string;
  workflowId: string;
};

export const getTransitionPayload = (inquiryEventData: InquiryEvent): TransitionPayload => {
  const { 'previous-step-name': prevStepName, 'next-step-name': nextStepName } =
    inquiryEventData.attributes;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return { prevStepName, nextStepName };
};

export const getConditionalPayload = (
  directRunData: DirectWorkflowRunEvent,
): ConditionalPayload => {
  const {
    'conditional-name': conditionalName,
    'route-index': routeIndex,
    'tree-node-token': treeNodeToken,
    'workflow-name': workflowName,
    'workflow-run-id': workflowRunId,
    'workflow-version-id': workflowVersionId,
    'workflow-id': workflowId,
  } = directRunData;
  return {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    conditionalName,
    routeIndex,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    treeNodeToken,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    workflowName,
    workflowRunId,
    workflowVersionId,
    workflowId,
  };
};

export const isInquiryRunEvent = (event: Event): boolean => {
  switch (event.attributes.name) {
    case EventName.InquiryTransitioned:
      // omit transition events with missing next-step-name values as they
      // cannot reliably be used to construct the highlight path.
      return (
        getTransitionPayload(event.attributes.payload.data as InquiryEvent).nextStepName != null
      );
    case EventName.InquiryRunConditionalRouted:
      return true;
    default:
      return false;
  }
};

export { fetchInquiryRunData };
